import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import {
  catchError,
  switchMap,
  map,
  filter,
  take,
  finalize,
} from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthService } from '../services/Auth/auth.service';
import { BrowserStorageService } from '../services/browser-storage/browser-storage.service';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { PlayerService } from '../services/music-player/player.service';
import { MobileVerificationFlowService } from '../services/Auth/mobile-verification-flow.service';
import {
  MobileActionWallType,
  VerificationContractService,
} from '../services/Auth/verification-contract.service';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  isRefreshingToken: boolean = false;
  tokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private storageService: BrowserStorageService,
    private ngxLoadingService: NgxSpinnerService,
    private authService: AuthService,
    private router: Router,
    private location: Location,
    private verificationContract: VerificationContractService,
    private injector: Injector
  ) {}

  // Tells the backend which platform the call came from, and carries the analytics
  // ids so its server sent events join the session this browser already has. Every
  // lookup is guarded: a missing value simply means the header is not sent.
  private analyticsHeaders(headers: any) {
    let updated = headers.set('X-Client-Platform', 'web');

    const gaClientId = this.readGaClientId();
    if (gaClientId) updated = updated.set('X-GA-Client-Id', gaClientId);

    const gaSessionId = this.readGaSessionId();
    if (gaSessionId) updated = updated.set('X-GA-Session-Id', gaSessionId);

    const phDistinctId = this.storageService.getPostHogDistinctId();
    if (phDistinctId) updated = updated.set('X-PH-Distinct-Id', phDistinctId);

    const fbp = this.readCookie('_fbp');
    if (fbp) updated = updated.set('X-FBP', fbp);

    const fbc = this.readCookie('_fbc');
    if (fbc) updated = updated.set('X-FBC', fbc);

    return updated;
  }

  // The Meta pixel writes _fbp and _fbc on the site's own domain, but the API is a
  // different origin and the app does not send credentials, so the backend never
  // receives them as cookies. They travel as headers instead, which is the only
  // thing that lets Meta attribute a signed out visitor.
  private readCookie(name: string): string {
    try {
      const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : '';
    } catch (e) {
      return '';
    }
  }

  // Parses the GA4 client id out of the _ga cookie (GA1.1.<client id>).
  private readGaClientId(): string {
    try {
      const match = document.cookie.match(/_ga=GA\d\.\d\.(\d+\.\d+)/);
      return match ? match[1] : '';
    } catch (e) {
      return '';
    }
  }

  // GA4 stores the session in _ga_<measurement id without the G- prefix>, shaped
  // GS1.1.<session id>.<session number>. Without it a server sent event counts
  // but sits outside the browser's session, so its traffic source reads (not set).
  private readGaSessionId(): string {
    try {
      const match = document.cookie.match(/_ga_[A-Z0-9]+=GS\d\.\d\.(\d+)/);
      return match ? match[1] : '';
    } catch (e) {
      return '';
    }
  }

  private updateDataCrossHeader(request: any) {
      const updatedHeaders = this.analyticsHeaders(request.headers)
          .set("Content-Security-Policy", "frame-ancestors 'self'")
          .set("X-Frame-Options", "SAMEORIGIN")
          .set("Cross-Origin-Opener-Policy", "same-origin")
        return request.clone({
          headers: updatedHeaders
        });
  }
  private updateToken(request: any, token: any) {
     const updatedHeaders = this.analyticsHeaders(request.headers)
          .set("Content-Security-Policy", "frame-ancestors 'self'")
          .set("X-Frame-Options", "SAMEORIGIN")
          .set("Cross-Origin-Opener-Policy", "same-origin")
          .set('Authorization', `Bearer ${token}`)
        return request.clone({
          headers: updatedHeaders
        });
  }

  isUserLogout() {
    this.storageService.clearAllSession();
    this.authService.currentUserDetails.next({});
    this.router.navigate(['/signin']);
    this.authService.closeAllDialogBox.next(true);
    setTimeout(() => {
      location.reload();
    },1000);
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const token: any = this.storageService.getSessionData();
    let authReq = request;
    if (
      token != null &&
      !authReq.url.includes('auth/refresh') &&
      !authReq.url.includes('amazonaws.com')
    ) {
      authReq = this.updateToken(request, token?.access_token);
    } else {
      if (!authReq.url.includes('amazonaws.com')) {
        authReq = this.updateDataCrossHeader(request);
      }

    }
    return next.handle(authReq).pipe(
      map(event => {
        if (event instanceof HttpResponse) {
          this.ngxLoadingService.hide();
        }
        return event;
    }),
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          const wallType = this.getMobileActionWallType(error, authReq?.url || '');
          if (wallType) {
            const musicPlayerService = this.injector.get(PlayerService);
            const mobileVerificationFlow =
              this.injector.get(MobileVerificationFlowService);
            musicPlayerService.stop();
            mobileVerificationFlow.triggerFromError(
              error,
              wallType,
              wallType === 'download'
            );
          }
        }
        let errorMessage = '';
        if (error?.status === 401) {
          errorMessage = 'Invalid token! Please sign in again.';
        } else if (error?.status === 400) {
          errorMessage = 'Your token has expired! Please sign in again.';
        }
        if (
          error instanceof HttpErrorResponse &&
          !authReq.url.includes('auth/signin') &&
          !authReq.url.includes('auth/refresh') &&
          error?.error?.message === errorMessage
        ) {
          return this.handle401Error(authReq, next);
        }
        if (authReq.url.includes('auth/refresh')) {
          this.isUserLogout();
        }
        let messageError: any = error?.error?.message;
        if (messageError && (messageError.includes("Unable to find beat") ||  messageError.includes("Invalid playlist slug"))) {
          setTimeout(() => {
            this.location.back();
          },500);
        }
        this.ngxLoadingService.hide();
        return throwError(error);
      }),
      finalize(() => this.ngxLoadingService.hide())
    );
  }

  handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.tokenSubject.next(null);

      const token = this.storageService.getRefreshAccessToken();

      if (token)
        return this.authService.refreshToken(token).pipe(
          switchMap((token: any) => {
            this.isRefreshingToken = false;

            const sessionData: any = this.storageService.getSessionData();
            if (sessionData) {
              sessionData.access_token = token.access_token;
              sessionData.refresh_token = token.refresh_token;
              this.storageService.setSessionData(sessionData);
              this.storageService.removedBpmDialogValue();
              this.tokenSubject.next(token.access_token);
            }

            return next.handle(this.updateToken(request, token.access_token));
          }),
          catchError((err) => {
            this.ngxLoadingService.hide();
            this.storageService.clearAllSession();
            this.isRefreshingToken = false;
            return throwError(err);
          })
        );
    }

    return this.tokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.updateToken(request, token)))
    );
  }

  private getMobileActionWallType(
    error: any,
    requestUrl: string
  ): MobileActionWallType | null {
    const wallType = this.verificationContract.getMobileActionWallType(error);
    if (!wallType) {
      return null;
    }

    if (wallType === 'play') {
      return wallType;
    }

    const normalizedUrl = (requestUrl || '').toLowerCase();
    const isDownloadRequest =
      normalizedUrl.includes('download') ||
      normalizedUrl.includes('read-object-url');
    if (!isDownloadRequest) {
      return null;
    }

    return wallType;
  }
}
